import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import Icon from '~/components/Icon/Icon';
import DOMPurify from 'dompurify';
import Translate from '../Translate/Translate';
import { returnCorrectPath, urlSeparator } from '../../modules/manageRouting';
import Tooltip from '@sumologic/yama-ds-components/components/Tooltip';

/**
 * General card container with base class
 * @author mm
 */
export default function OperationCard({ children }) {
    return <div className="operation-card">{children}</div>;
}

OperationCard.propTypes = {
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.array])
};

/**
 * Provide a link to user page
 * @author mm
 */
export function UserLink({ user }) {
    const { type, id, name } = user;
    if (type === 'user') {
        return <Link to={`/users_management|${id}`}>{name}</Link>;
    }
    if (type === 'group') {
        return <Link to={`/groups_management|${id}`}>{name}</Link>;
    }
    /*if (type === 'queue') {
        return <Link to={`/setting_general`}>{name}</Link>;
    }*/
    if (type === 'mssp') {
        return (
            <Link to={`/users_management|${id}`}>
                <Icon type="group-work" className="ms-1" /> {name}
            </Link>
        );
    }
    return null;
}

UserLink.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
        name: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired
    }).isRequired
};

/**
 * Provide a link to incident page
 * @author mm
 */
export function IncidentLink({ incident, children, onClick = () => {} }) {
    return (
        <Link
            className="incident"
            to={returnCorrectPath('incidentsDetails', false, incident.id)}
            onClick={onClick}
        >
            {children || incident.name}
        </Link>
    );
}

IncidentLink.propTypes = {
    incident: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
        name: PropTypes.string.isRequired
    }).isRequired,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.array])
};

/**
 * Provide a link to runbook page
 * @author mm
 */
export function RunbookLink({ runbook, incident, children }) {
    // mm: if runbook has no incident there is no link to the runbook (only to triage event)
    if (!incident) return children;
    const url = `/incident|${incident.id}|playbook|${runbook.id}|history|${runbook.running_id}`;
    return (
        <Link className="runbook" to={url}>
            {children || runbook.name}
        </Link>
    );
}

RunbookLink.propTypes = {
    runbook: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        running_id: PropTypes.string.isRequired
    }).isRequired,
    incident: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
        name: PropTypes.string.isRequired
    }),
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.array])
};

/**
 * Provide a link to triage event page
 * @author mm
 */
export function TriageEventLink({ triageEvent, incident, children }) {
    const url = returnCorrectPath('triageDetails', false, triageEvent.id);
    return (
        <Link className="triage" to={url}>
            {children || triageEvent.name}
        </Link>
    );
}

TriageEventLink.propTypes = {
    triageEvent: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
        name: PropTypes.string
    }).isRequired,
    incident: PropTypes.shape({
        id: PropTypes.number.isRequired
    }),
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.array])
};

/**
 * Provide a link to task details page
 * @author mm
 */
export function TaskLink({ task, incident, triageEvent, children, className }) {
    let url = `/tasks|${task.id}`;
    if (incident) url = `/incident|${incident.id}|tasks|${task.id}`;
    else if (triageEvent) url = `/triage|${triageEvent.id}`;
    return (
        <Link to={url} className={className}>
            {children || task.title}
        </Link>
    );
}

TaskLink.propTypes = {
    task: PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string
    }).isRequired,
    incident: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
    }),
    triageEvent: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
        name: PropTypes.string
    }),
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]),
    className: PropTypes.string
};

/**
 * Provide a link to playbook details page
 * @author rn
 */
export function PlaybookLink({
    playbook,
    incident,
    triageEvent,
    children,
    className
}) {
    let url = '';
    if (incident) {
        url = `${returnCorrectPath(
            'incidentsTasks',
            false,
            incident.id
        )}${urlSeparator}${playbook.id}`;
    } else if (triageEvent) {
        url = returnCorrectPath('triageDetails', false, triageEvent.id);
    }
    return (
        <Link to={url} className={className}>
            {children || playbook.title}
        </Link>
    );
}

PlaybookLink.propTypes = {
    playbook: PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string
    }).isRequired,
    incident: PropTypes.shape({
        id: PropTypes.number.isRequired
    }),
    triageEvent: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
        name: PropTypes.string
    }),
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]),
    className: PropTypes.string
};

/**
 * Create a list of icons with link to related page
 * @author mm
 */
export function RelationshipIcons({ incident, triageEvent, runbook, isArk }) {
    return (
        <div className="relationship-icons">
            {incident && incident.id > 0 && (
                <IncidentLink incident={incident}>
                    <Tooltip
                        title={
                            <>
                                <Translate message="Incident" />
                                {':\u0020'}
                                {incident.name}
                            </>
                        }
                        placement="top"
                        arrow
                        classes={{ tooltip: 'incident' }}
                    >
                        <span>
                            <Icon type="shield-check2" className="me-2" />
                        </span>
                    </Tooltip>
                </IncidentLink>
            )}
            {triageEvent && (
                <TriageEventLink triageEvent={triageEvent} incident={incident}>
                    <Tooltip
                        title={
                            <>
                                <span>{SETTINGS.triageSectionName}</span>
                                {':\u0020'}
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            triageEvent.name
                                        )
                                    }}
                                />
                            </>
                        }
                        placement="top"
                        arrow
                        classes={{ tooltip: 'triage' }}
                    >
                        <span>
                            <Icon type="arrows-split" className="me-2" />
                        </span>
                    </Tooltip>
                </TriageEventLink>
            )}
            {runbook && (
                <RunbookLink runbook={runbook} incident={incident}>
                    <Tooltip
                        title={
                            <>
                                <Translate message="Playbook" />
                                {':\u0020'}
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(runbook.name)
                                    }}
                                />
                            </>
                        }
                        placement="top"
                        arrow
                        classes={{ tooltip: 'runbook' }}
                    >
                        <span>
                            <Icon type="site-map" className="me-2" />
                        </span>
                    </Tooltip>
                </RunbookLink>
            )}
            {/*{isArk && (
                <Tooltip
                    title={<Translate message="Recommended by Ark" />}
                    placement="top"
                    arrow
                    className="is-ark"
                >
                    <span>
                        <Icon type="ark" />
                    </span>
                </Tooltip>
            )}*/}
        </div>
    );
}

RelationshipIcons.propTypes = {
    incident: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
            .isRequired,
        name: PropTypes.string.isRequired
    }),
    runbook: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
    }),
    triageEvent: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string.isRequired
    })
};
